import axios from 'axios';

const getApiBaseUrl = () => {
  const isProduction = process.env.NODE_ENV === 'production';

  if (isProduction) {
    return process.env.REACT_APP_API_BASE_URL || 'https://api.gethomecrowd.com/api';
  }

  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return `${window.location.origin}/api`;
  }

  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const response = await axios.post(`${API_BASE_URL}/token/refresh/`, {
                refresh: refreshToken,
              });

              const { access } = response.data;
              localStorage.setItem('access_token', access);
              originalRequest.headers.Authorization = `Bearer ${access}`;
              return this.api(originalRequest);
            }
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            window.location.href = '/login/';
          } catch (refreshError) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            window.location.href = '/login/';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  storeSession(data, status) {
    const { user, tokens } = data;
    localStorage.setItem('access_token', tokens.access);
    localStorage.setItem('refresh_token', tokens.refresh);
    localStorage.setItem('user', JSON.stringify(user));
    return { user, tokens, created: status === 201 };
  }

  async login(email, password) {
    const response = await this.api.post('/auth/login/', { email, password });
    return this.storeSession(response.data, response.status);
  }

  async register(payload) {
    const response = await this.api.post('/auth/register/', payload);
    return this.storeSession(response.data, response.status);
  }

  async googleLogin(idToken) {
    const response = await this.api.post('/auth/google/', {
      id_token: idToken,
      accepted_terms_and_policies: true,
    });
    return this.storeSession(response.data, response.status);
  }

  async appleLogin({ identityToken, firstName, lastName }) {
    const response = await this.api.post('/auth/apple/', {
      identity_token: identityToken,
      first_name: firstName || '',
      last_name: lastName || '',
    });
    return this.storeSession(response.data, response.status);
  }

  async forgotPassword(email) {
    const response = await this.api.post('/auth/forgot-password/', { email });
    return response.data;
  }

  async getPublicSchools() {
    const response = await this.api.get('/school/public-schools/');
    return response.data;
  }

  async assignSchool(schoolId) {
    const response = await this.api.post('/assign-school/', { school_id: schoolId });
    return response.data;
  }

  async verifyEmail(token) {
    const response = await this.api.post('/users/verify-email/', { token });
    return response.data;
  }

  async getPublicProfile(fanId) {
    const response = await axios.get(
      `${API_BASE_URL}/users/public-profile/${encodeURIComponent(String(fanId || '').trim())}/`
    );
    return response.data;
  }

  async getPublicSchoolBranding(schoolId) {
    const response = await axios.get(
      `${API_BASE_URL}/school/merchants-page-config/${encodeURIComponent(String(schoolId || '').trim())}/`
    );
    return response.data;
  }

  async resetPassword({ uid, token, new_password }) {
    const response = await this.api.post('/auth/reset-password/', {
      uid,
      token,
      new_password,
    });
    return response.data;
  }

  async fetchCurrentUser() {
    try {
      const response = await this.api.get('/users/users/profile/');
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      try {
        const response = await this.api.get('/auth/me/');
        localStorage.setItem('user', JSON.stringify(response.data));
        return response.data;
      } catch (authError) {
        return this.getCurrentUser();
      }
    }
  }

  async getPaymentCards() {
    const response = await this.api.get('/payment-cards/');
    return response.data;
  }

  async updateUserProfile(profileData) {
    const response = await this.api.patch('/users/users/profile/', profileData);
    if (response.data?.id) {
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    }
    return this.fetchCurrentUser();
  }

  async changePassword({ current_password, new_password }) {
    const payload = { new_password };
    if (current_password) payload.current_password = current_password;
    const response = await this.api.post('/auth/change-password/', payload);
    const { tokens, user } = response.data || {};
    if (tokens?.access && tokens?.refresh) {
      localStorage.setItem('access_token', tokens.access);
      localStorage.setItem('refresh_token', tokens.refresh);
    }
    if (user) localStorage.setItem('user', JSON.stringify(user));
    return response.data;
  }

  async deleteAccount(password) {
    const response = await this.api.post('/auth/delete-account/', { password });
    this.logout();
    return response.data;
  }

  async resendVerificationEmail() {
    const response = await this.api.post('/users/resend-verification-email/');
    return response.data;
  }

  async createOliveMember() {
    const response = await this.api.post('/olive/createMember');
    return response.data;
  }

  async addCardDirect(cardData) {
    const response = await this.api.post('/olive/addCardDirect', cardData);
    return response.data;
  }

  async deactivateCard(cardId) {
    const response = await this.api.post('/olive/deactivateCard', { cardId });
    return response.data;
  }

  async claimSetupTaskReward(task) {
    const response = await this.api.post('/rewards/setup-task-rewards/claim/', { task });
    return response.data;
  }

  async getSetupTaskRewards() {
    const response = await this.api.get('/rewards/setup-task-rewards/');
    return response.data;
  }

  async syncSetupTaskRewards() {
    const response = await this.api.post('/rewards/setup-task-rewards/sync/');
    return response.data;
  }

  async getFirstRewards() {
    const response = await this.api.get('/embed/v1/rewards/first-rewards/');
    return response.data;
  }

  async getFirstReward(rewardId) {
    const response = await this.api.get(`/embed/v1/rewards/first-rewards/${rewardId}/`);
    return response.data;
  }

  async redeemFirstReward(rewardId, { contactMethod, contactValue }) {
    const response = await this.api.post(`/embed/v1/rewards/first-rewards/${rewardId}/redeem/`, {
      contactMethod,
      contactValue,
    });
    return response.data;
  }

  async getRewards() {
    const response = await this.api.get('/rewards/rewards/');
    return response.data;
  }

  async getReward(rewardId) {
    const response = await this.api.get(`/rewards/rewards/${rewardId}/`);
    return response.data;
  }

  async createRedemption(rewardId, pointsRequired, extra = {}) {
    const payload = {
      reward: rewardId,
      points_spent: pointsRequired,
    };
    if (extra.ageConfirmed) {
      payload.age_confirmed = true;
    }
    const response = await this.api.post('/rewards/redemptions/', payload);
    return response.data;
  }

  async createStripeRewardCheckoutSession(rewardId) {
    const response = await this.api.post('/rewards/stripe/create-checkout-session/', {
      reward_id: rewardId,
    });
    return response.data;
  }

  async placeBid(auctionId, amount) {
    const response = await this.api.post(`/rewards/auctions/${auctionId}/bid/`, { amount });
    return response.data;
  }

  async getLeaderboard() {
    const response = await this.api.get('/users/leaderboard/');
    return response.data;
  }

  getApiOrigin() {
    return API_BASE_URL.replace(/\/api$/, '');
  }

  async createTravelSession() {
    const response = await this.api.post('/travel/session/', {});
    return response.data;
  }

  async getSportSchedules(schoolId) {
    const response = await this.api.get('/school/sport-schedules/', {
      params: schoolId ? { school: schoolId } : undefined,
    });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
  }

  async getScheduleEvents(scheduleId) {
    const response = await this.api.get('/school/schedule-events/', {
      params: scheduleId ? { schedule: scheduleId } : undefined,
    });
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
  }

  async createScheduleEventCheckIn(eventId, payload) {
    const response = await this.api.post(
      `/school/schedule-events/${encodeURIComponent(String(eventId))}/check-in/`,
      {
        latitude: payload && payload.latitude,
        longitude: payload && payload.longitude,
      }
    );
    return response.data;
  }

  async createScheduleEventPrediction(eventId, payload) {
    const response = await this.api.post(
      `/school/schedule-events/${encodeURIComponent(String(eventId))}/predict/`,
      {
        home_score: payload && payload.home_score,
        away_score: payload && payload.away_score,
      }
    );
    return response.data;
  }

  async getScheduleEventPredictions(eventId, { offset = 0, limit = 50 } = {}) {
    const response = await this.api.get(
      `/school/schedule-events/${encodeURIComponent(String(eventId))}/predictions/`,
      { params: { offset, limit } }
    );
    return response.data;
  }

  async getBadges() {
    const response = await this.api.get('/rewards/badges/');
    return response.data;
  }

  async getUserActivityLog(limit = 8) {
    const response = await this.api.get('/rewards/user/activity/', {
      params: { limit },
    });
    return response.data.activity_log || [];
  }

  async getWildfireOffers({ page = 1, pageSize = 12 } = {}) {
    const response = await this.api.get('/wildfire/offers/', {
      params: { page, pageSize },
    });
    return response.data;
  }

  async getFeaturedOffers(params = {}) {
    const response = await this.api.get('/merchant/featured-offers/', { params });
    return response.data;
  }

  async getOliveOffers({ latitude, longitude, page = 1, pageSize = 20, includeOnline = false } = {}) {
    const params = { page, pageSize };
    if (includeOnline === false) params.includeOnline = 'false';
    if (latitude != null && longitude != null) {
      params.latitude = latitude;
      params.longitude = longitude;
    }
    const response = await this.api.get('/olive/offers/', { params });
    return response.data;
  }

  async trackWildfireClick(merchantId) {
    const response = await this.api.get('/wildfire/track-click/', {
      params: { merchant_id: merchantId },
    });
    return response.data;
  }

  async getWildfireMerchantDetail(merchantId) {
    const response = await this.api.get('/wildfire/merchant/', {
      params: { merchant_id: merchantId },
    });
    return response.data;
  }

  async getOfferDetails(offerId) {
    const response = await this.api.get(`/olive/offers/${offerId}/`);
    return response.data;
  }

  async trackOfferClick(offerId) {
    const response = await this.api.get('/olive/track-click/', {
      params: { offer_id: offerId },
    });
    return response.data;
  }

  async getMySupportThread() {
    const response = await this.api.get('/users/support-thread/');
    return response.data;
  }

  async replyToSupportThread(body, files = []) {
    if (files.length) {
      const formData = new FormData();
      if (body) formData.append('body', body);
      files.forEach((file) => formData.append('attachments', file));
      const response = await this.api.post('/users/support-thread/reply/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
    const response = await this.api.post('/users/support-thread/reply/', { body });
    return response.data;
  }

  async markMySupportThreadRead() {
    const response = await this.api.post('/users/support-thread/read/');
    return response.data;
  }

  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated() {
    return !!localStorage.getItem('access_token');
  }
}

const apiService = new ApiService();
export default apiService;
